// src/pages/Fab/DivisionPage.jsx
import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import "./DivisionPage.css"
import { getUpdatedBracketWithWinners } from '../BracketCreation/Utils/bracketUtils.js'
import BracketDisplay from '../BracketCreation/Core/BracketDisplay.jsx'
import CreateGameModal from './CreateGameModal.jsx';
import EditGameModal from './EditGameModal';
import BracketControlsButtons from './BracketControlsButtons.jsx';
import DivisionWinnerBanner from './DivisionWinnerBanner.jsx'
import DivisionTeamsTable from "./DivisionTeamsTable.jsx";
import { fetchDivision, fetchGames, createGame, deleteGame, updateGame } from './divisionApi.js'
import {
  isTeamInBracket as utilIsTeamInBracket,
  teamHasScore as utilTeamHasScore,
  removeTeamFromBracket as utilRemoveTeamFromBracket,
} from "../BracketCreation/Utils/bracketTeamUtils.js";
import Breadcrumbs from "../../../../../../components/Breadcrumbs.jsx"
import api from "@api";

function DivisionPage() {
  const { seasonId, divisionId } = useParams();
  const [division, setDivision] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bracketData, setBracketData] = useState(null);
  const [selectedMatchup, setSelectedMatchup] = useState(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [bracketTeamCount, setBracketTeamCount] = useState(8);
  const [isBracketSaved, setIsBracketSaved] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [gameForm, setGameForm] = useState({
    date: '',
    time: '',
    location: '',
    location_url: '',
  });
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editGame, setEditGame] = useState(null);
  const [games, setGames] = useState([]);

  const [savedLocations, setSavedLocations] = useState([]);
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [showCreateLocationModal, setShowCreateLocationModal] = useState(false);
  const [showEditLocationsModal, setShowEditLocationsModal] = useState(false);
  const [newSavedLocationForm, setNewSavedLocationForm] = useState({
    name: '',
    location_url: '',
  });
  const [editingSavedLocations, setEditingSavedLocations] = useState([]);
  const [availableTeams, setAvailableTeams] = useState([]);
  const [availableTeamSort, setAvailableTeamSort] = useState("az");
  const [showAllAvailableTeams, setShowAllAvailableTeams] = useState(false);
  const [showBracketTeamEditor, setShowBracketTeamEditor] = useState(false);
  const navigate = useNavigate();
  // UE1
  useEffect(() => {
    if (!bracketData || isBracketSaved) return;
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [bracketData, isBracketSaved]);

  // UE2
  useEffect(() => {
    if (!initialLoadDone) {
      setInitialLoadDone(true);
      if (division?.bracket_json) {
        try {
          const parsed = typeof division.bracket_json === "string"
            ? JSON.parse(division.bracket_json)
            : division.bracket_json;
          setBracketData(parsed);
        } catch (err) {
          console.error("❌ Error parsing saved bracket:", err);
        }
      } else {
        setBracketData(null);
      }
      return;
    }
    if (division?.bracket_json) {
      try {
        const parsed = typeof division.bracket_json === "string"
          ? JSON.parse(division.bracket_json)
          : division.bracket_json;
        setBracketData(parsed);
      } catch (err) {
        console.error("❌ Error parsing saved bracket:", err);
      }
    }
  }, [division?.bracket_json, initialLoadDone]);

  // UE3
  useEffect(() => {
    if (division?.bracket_json) {
      setIsBracketSaved(true);
    } else {
      setIsBracketSaved(false);
    }
  }, [division?.bracket_json]);


  // UE4
  // UE4
  useEffect(() => {
    const loadGames = async () => {
      try {
        const games = await fetchGames(seasonId, divisionId);
        setGames(games);
      } catch (err) {
        console.error("Error fetching games:", err);
      }
    };
    loadGames();
  }, [seasonId, divisionId]);

  useEffect(() => {
    const loadSavedLocations = async () => {
      try {
        const res = await api.get(`/api/saved-locations`, {
          withCredentials: true,
        });
        const locations = res.data || [];
        setSavedLocations(locations);
        setEditingSavedLocations(locations);
      } catch (err) {
        console.error("Error fetching saved locations:", err);
      }
    };

    loadSavedLocations();
  }, []);
  // UE5
  useEffect(() => {
    const loadDivision = async () => {
      try {
        const data = await fetchDivision(seasonId, divisionId);
        const sortedDivision = {
          ...data,
          teams: [...(data.teams || [])].sort((a, b) => (a.position ?? 999) - (b.position ?? 999)),
        };
        setDivision(sortedDivision);
      } catch (err) {
        console.error("Error fetching division:", err);
      } finally {
        setLoading(false);
      }
    };
    loadDivision();
  }, [seasonId, divisionId]);
  useEffect(() => {
    const loadAvailableTeams = async () => {
      try {
        const res = await api.get(
          `/api/seasonsv2/${seasonId}/leagues-with-teams-public`,
          { withCredentials: true }
        );

        const leaguesData = res.data.leagues || [];

        const allTeams = leaguesData.flatMap((league) =>
          league.teams?.map((team) => ({
            ...team,
            leagueName: league.name,
          })) || []
        );

        setAvailableTeams(allTeams);
      } catch (err) {
        console.error("Error fetching available teams:", err);
      }
    };

    loadAvailableTeams();
  }, [seasonId]);

  // CONST1
  const bracketWithWinners = useMemo(() => {
    if (!bracketData || games.length === 0) return bracketData;
    const updatedRounds = getUpdatedBracketWithWinners(bracketData, games);
    return {
      ...bracketData,
      rounds: updatedRounds,
    };
  }, [bracketData, games]);

  // UE6 — sync winner-updated bracket into real bracketData
  useEffect(() => {
    if (!bracketWithWinners) return;

    // Compare rounds only
    const updated = JSON.stringify(bracketWithWinners.rounds);
    const current = JSON.stringify(bracketData?.rounds);

    // If nothing changed, do nothing
    if (updated === current) return;

    console.log("⏩ Updating bracketData with winners...");
    setBracketData(bracketWithWinners);
  }, [bracketWithWinners]);

  // CONST2
  const handleCreateGame = async () => {
    const existing = games.find(g => g.match_id === selectedMatchup.matchId);
    if (existing) {
      alert("A game already exists for this matchup. Edit it instead.");
      return;
    }
    try {
      console.log("Creating game payload:", {
        home_team_id: selectedMatchup.teamA?.slt_id || null,
        away_team_id: selectedMatchup.teamB?.slt_id || null,
        date: gameForm.date,
        time: gameForm.time,
        location: gameForm.location,
        location_url: gameForm.location_url,
        match_id: selectedMatchup.matchId,
      });
      const res = await createGame(seasonId, divisionId, {
        home_team_id: selectedMatchup.teamA?.slt_id || null,
        away_team_id: selectedMatchup.teamB?.slt_id || null,
        date: gameForm.date,
        time: gameForm.time,
        location: gameForm.location,
        location_url: gameForm.location_url,
        match_id: selectedMatchup.matchId,
      });
      alert("Game created!");
      setGames((prev) => [...prev, res.game]);
      setIsCreateModalOpen(false);
      setGameForm({ date: '', time: '', location: '', location_url: '' });
    } catch (err) {
      console.error("Failed to create game:", err);
      alert(err.response?.data?.message || "Error creating game");
    }
  };

  // CONST3
  const handleEditGame = async (updatedGame) => {
    try {
      await updateGame(seasonId, divisionId, updatedGame.id, updatedGame);
      // Reload games
      const refreshed = await fetchGames(seasonId, divisionId);
      setGames(refreshed);
      setIsEditModalOpen(false);
      setEditGame(null);
    } catch (err) {
      alert("Failed to update game.");
    }
  };


  // CONST 4
  const handleDeleteGame = async (gameId) => {
    if (!window.confirm("Are you sure you want to delete this game?")) return;

    try {
      const res = await deleteGame(seasonId, divisionId, gameId);
      alert("Game deleted!");

      // Remove deleted game from frontend state right away
      setGames((prev) => prev.filter((g) => g.id !== gameId));

      if (res?.bracket_json) {
        setBracketData(res.bracket_json);

        setDivision((prev) => ({
          ...prev,
          bracket_json: res.bracket_json,
        }));
      }

      // Then fetch fresh games from backend
      const newGames = await fetchGames(seasonId, divisionId);
      setGames(newGames);
    } catch (err) {
      console.error("Failed to delete game:", err);
      alert("Error deleting game");
    }
  };
  const refreshSavedLocations = async () => {
    try {
      const res = await api.get(`/api/saved-locations`, {
        withCredentials: true,
      });

      const locations = res.data || [];
      setSavedLocations(locations);
      setEditingSavedLocations(locations);
    } catch (err) {
      console.error("Error refreshing saved locations:", err);
    }
  };

  const handleCreateSavedLocation = async () => {
    if (!newSavedLocationForm.name.trim()) {
      return alert("Location name is required.");
    }

    try {
      await api.post(
        `/api/saved-locations`,
        {
          name: newSavedLocationForm.name,
          location_url: newSavedLocationForm.location_url,
        },
        { withCredentials: true }
      );

      setNewSavedLocationForm({ name: '', location_url: '' });
      setShowCreateLocationModal(false);
      await refreshSavedLocations();
    } catch (err) {
      console.error("Failed to create saved location:", err);
      alert("Failed to create saved location.");
    }
  };

  const handleEditSavedLocationField = (id, field, value) => {
    setEditingSavedLocations((prev) =>
      prev.map((loc) =>
        loc.id === id ? { ...loc, [field]: value } : loc
      )
    );
  };

  const handleSaveEditedLocations = async () => {
    try {
      await Promise.all(
        editingSavedLocations.map((loc) =>
          api.put(
            `/api/saved-locations/${loc.id}`,
            {
              name: loc.name,
              location_url: loc.location_url,
            },
            { withCredentials: true }
          )
        )
      );

      setShowEditLocationsModal(false);
      await refreshSavedLocations();
    } catch (err) {
      console.error("Failed to save edited locations:", err);
      alert("Failed to save locations.");
    }
  };

  const handleDeleteSavedLocation = async (id) => {
    if (!window.confirm("Delete this saved location?")) return;

    try {
      await api.delete(`/api/saved-locations/${id}`, {
        withCredentials: true,
      });

      await refreshSavedLocations();
    } catch (err) {
      console.error("Failed to delete saved location:", err);
      alert("Failed to delete saved location.");
    }
  };

  const handleSelectSavedLocation = (loc) => {
    setGameForm((prev) => ({
      ...prev,
      location: loc.name || '',
      location_url: loc.location_url || '',
    }));
    setLocationSuggestions([]);
  };


  function isTeamInBracket(teamId) {
    return utilIsTeamInBracket(teamId, bracketData);
  }

  function teamHasScore(teamId) {
    return utilTeamHasScore(teamId, games);
  }

  function removeTeamFromBracket(teamId) {
    utilRemoveTeamFromBracket(
      teamId,
      bracketData,
      division.id,
      games
    ).then(updated => {
      setBracketData(updated);
    });
  }
  const teamsNotInTournament = availableTeams
    .filter((team) => {
      const alreadyAddedToCurrentDivision = division?.teams?.some(
        (divisionTeam) => divisionTeam.slt_id === team.slt_id
      );

      const alreadyInAnotherTournament =
        team.tournament_division_id &&
        Number(team.tournament_division_id) !== Number(divisionId);

      return !alreadyAddedToCurrentDivision && !alreadyInAnotherTournament;
    })
    .map((team) => ({
      ...team,
      winPercentage:
        (team.wins ?? 0) + (team.losses ?? 0) > 0
          ? (team.wins ?? 0) / ((team.wins ?? 0) + (team.losses ?? 0))
          : 0,
    }))
    .sort((a, b) => {
      if (availableTeamSort === "ranking") {
        if (b.winPercentage !== a.winPercentage) {
          return b.winPercentage - a.winPercentage;
        }

        return (
          (a.losses ?? 0) - (b.losses ?? 0) ||
          (b.wins ?? 0) - (a.wins ?? 0) ||
          a.name.localeCompare(b.name)
        );
      }

      return a.name.localeCompare(b.name);
    });

  const displayedAvailableTeams = teamsNotInTournament;


  const handleAddTeamToTournament = async (team) => {
    try {
      const newPosition = division?.teams?.length ?? 0;

      await api.post(
        `/api/divisions/${divisionId}/teams`,
        {
          seasons_leagues_teams_id: team.slt_id,
          position: newPosition,
        },
        { withCredentials: true }
      );

      setDivision((prev) => ({
        ...prev,
        teams: [
          ...(prev.teams || []),
          {
            ...team,
            id: team.slt_id,
            slt_id: team.slt_id,
            position: newPosition,
          }
        ],
      }));
    } catch (err) {
      console.error("Error adding team to tournament:", err);
      alert(err.response?.data?.message || "Failed to add team.");
    }
  };


  const handleRemoveTeamFromTournament = async (team) => {
    const teamKey = team.slt_id ?? team.id;

    if (isTeamInBracket(teamKey) && teamHasScore(teamKey)) {
      alert("This team already has a score in the bracket and cannot be removed.");
      return;
    }

    const bracketExists = !!bracketData;

    if (bracketExists) {
      const confirmRemove = window.confirm(
        `Remove ${team.name} from this tournament and bracket?`
      );

      if (!confirmRemove) return;
    }

    try {
      await api.delete(`/api/divisions/${divisionId}/teams/${teamKey}`, {
        withCredentials: true,
      });

      setDivision((prev) => ({
        ...prev,
        teams: (prev.teams || []).filter((t) => (t.slt_id ?? t.id) !== teamKey),
      }));
    } catch (err) {
      console.error("Error removing team from tournament:", err);
      alert(err.response?.data?.message || "Failed to remove team.");
    }
  };



  if (loading) return <div></div>;
  if (!division) return <div></div>;

  const hasBracket = !!bracketData;

  return (
    <div className="division-page">

      <div className="division-info">

        <div className="division-page-breadcrumbs">
          <button
            type="button"
            className="division-page-breadcrumb-link"
            onClick={() => navigate("/dashboard/seasons")}
          >
            Seasons
          </button>

          <span className="division-page-breadcrumb-separator">&gt;</span>

          <button
            type="button"
            className="division-page-breadcrumb-link"
            onClick={() => navigate(`/dashboard/seasons/${seasonId}?tab=tournament`)}
          >
            Tournaments
          </button>

          <span className="division-page-breadcrumb-separator">&gt;</span>

          <span className="division-page-breadcrumb-current">
            {division?.name || "Tournament Bracket"}
          </span>
        </div>
        <div className="tournament-bracket-action-card">
          <div className="tournament-bracket-action-text">
            <h2>
              {hasBracket ? "Bracket Actions" : "Bracket Setup"}
            </h2>

            <p>
              {hasBracket
                ? "Manage this generated bracket or adjust tournament teams."
                : "Choose a bracket size and generate the tournament bracket."}
            </p>
          </div>

          <div className="tournament-bracket-controls-row">
            <BracketControlsButtons
              division={division}
              bracketData={bracketData}
              setBracketData={setBracketData}
              isBracketSaved={isBracketSaved}
              setIsBracketSaved={setIsBracketSaved}
              showManual={showManual}
              setShowManual={setShowManual}
              bracketTeamCount={bracketTeamCount}
              setBracketTeamCount={setBracketTeamCount}
              setDivision={setDivision}
              setGames={setGames}
            />

            {hasBracket && (
              <button
                type="button"
                className="tournament-bracket-edit-teams-btn"
                onClick={() => setShowBracketTeamEditor((prev) => !prev)}
              >
                {showBracketTeamEditor ? "View Bracket" : "Add / Remove Teams"}
              </button>
            )}
          </div>
        </div>

        <div className={hasBracket ? "tournament-bracket-layout" : "tournament-team-layout"}>
          <div className="tournament-team-card">

            <DivisionTeamsTable
              teams={division.teams}
              isTeamInBracket={isTeamInBracket}
              teamHasScore={teamHasScore}
              removeTeamFromBracket={removeTeamFromBracket}
              removeTeamFromTournament={handleRemoveTeamFromTournament}
            />
          </div>
          {!hasBracket && (
          <div className="tournament-team-card">
            <div className="available-teams-header">
                <div className="available-team-sort">
                  <button
                    type="button"
                    className={availableTeamSort === "az" ? "active" : ""}
                    onClick={() => setAvailableTeamSort("az")}
                  >
                    A-Z
                  </button>

                  <button
                    type="button"
                    className={availableTeamSort === "ranking" ? "active" : ""}
                    onClick={() => setAvailableTeamSort("ranking")}
                  >
                    Ranking
                  </button>
                  {teamsNotInTournament.length > 20 && (
                    <button
                      type="button"
                      className={showAllAvailableTeams ? "active" : ""}
                      onClick={() => setShowAllAvailableTeams((prev) => !prev)}
                    >
                      {showAllAvailableTeams ? "Show Less" : `Show All (${teamsNotInTournament.length})`}
                    </button>
                  )}
                </div>

              <h3>Available Teams</h3>

            </div>
              
              <div
                className={`add-tournament-teams-box ${showAllAvailableTeams ? "show-all" : ""}`}
              >
                {teamsNotInTournament.length === 0 ? (
                  <p className="add-tournament-empty">No more teams available.</p>
                ) : (
                  <table className="available-teams-table">
                    <thead>
                      <tr>
                        <th>Pos</th>
                        <th>Team</th>
                        <th>League</th>
                        <th>W-L</th>
                        <th></th>
                      </tr>
                    </thead>

                    <tbody>
                      {displayedAvailableTeams.map((team, i) => (
                        <tr key={team.slt_id}>
                          <td>{i + 1}</td>
                          <td>{team.name}</td>
                          <td>{team.leagueName || "—"}</td>
                          <td>
                            {team.wins ?? 0}-{team.losses ?? 0}
                          </td>
                          <td>
                            <button
                              type="button"
                              onClick={() => handleAddTeamToTournament(team)}
                            >
                              Add
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
          </div>
          )}
          {hasBracket && (
            <div className="tournament-bracket-card">
              {showBracketTeamEditor ? (
                <div className="tournament-team-card tournament-bracket-editor-card">
                  <div className="available-teams-header">
                    <div className="available-team-sort">
                      <div className="available-team-sort">
                        <button
                          type="button"
                          className={availableTeamSort === "az" ? "active" : ""}
                          onClick={() => setAvailableTeamSort("az")}
                        >
                          A-Z
                        </button>

                        <button
                          type="button"
                          className={availableTeamSort === "ranking" ? "active" : ""}
                          onClick={() => setAvailableTeamSort("ranking")}
                        >
                          Ranking
                        </button>
                      </div>
                    </div>

                    <h3>Available Teams</h3>

                  </div>

                  <div className="available-team-sort">
                    <div className="add-tournament-teams-box">
                      {teamsNotInTournament.length === 0 ? (
                        <p className="add-tournament-empty">No more teams available.</p>
                      ) : (
                        <table className="available-teams-table">
                          <thead>
                            <tr>
                              <th>Pos</th>
                              <th>Team</th>
                              <th>League</th>
                              <th>W-L</th>
                              <th></th>
                            </tr>
                          </thead>

                          <tbody>
                            {displayedAvailableTeams.map((team, i) => (
                              <tr key={team.slt_id}>
                                <td>{i + 1}</td>
                                <td>{team.name}</td>
                                <td>{team.leagueName || "—"}</td>
                                <td>
                                  {team.wins ?? 0}-{team.losses ?? 0}
                                </td>
                                <td>
                                  <button
                                    type="button"
                                    onClick={() => handleAddTeamToTournament(team)}
                                  >
                                    Add
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <BracketDisplay
                  bracketData={bracketWithWinners}
                  games={games}
                  setGames={setGames}
                  fetchGames={fetchGames}
                  navigate={navigate}
                  setSelectedMatchup={setSelectedMatchup}
                  setIsCreateModalOpen={setIsCreateModalOpen}
                  handleDeleteGame={handleDeleteGame}
                  setEditGame={setEditGame}
                  setIsEditModalOpen={setIsEditModalOpen}
                  division={division}
                  setBracketData={setBracketData}
                />
              )}
            </div>
          )}
        </div>
      </div>

  
      <DivisionWinnerBanner bracketData={bracketData} games={games} />
      <CreateGameModal
        isOpen={isCreateModalOpen}
        selectedMatchup={selectedMatchup}
        gameForm={gameForm}
        setGameForm={setGameForm}
        onCreate={handleCreateGame}
        onCancel={() => setIsCreateModalOpen(false)}

        savedLocations={savedLocations}
        locationSuggestions={locationSuggestions}
        setLocationSuggestions={setLocationSuggestions}

        showCreateLocationModal={showCreateLocationModal}
        setShowCreateLocationModal={setShowCreateLocationModal}
        showEditLocationsModal={showEditLocationsModal}
        setShowEditLocationsModal={setShowEditLocationsModal}

        newSavedLocationForm={newSavedLocationForm}
        setNewSavedLocationForm={setNewSavedLocationForm}
        editingSavedLocations={editingSavedLocations}

        handleCreateSavedLocation={handleCreateSavedLocation}
        handleEditSavedLocationField={handleEditSavedLocationField}
        handleSaveEditedLocations={handleSaveEditedLocations}
        handleDeleteSavedLocation={handleDeleteSavedLocation}
        handleSelectSavedLocation={handleSelectSavedLocation}
      />
      <EditGameModal
        isOpen={isEditModalOpen}
        originalGame={editGame}
        onEdit={handleEditGame}
        onCancel={() => {
          setIsEditModalOpen(false);
          setEditGame(null);
        }}
      />

    </div>
  );
}

export default DivisionPage;
